import SalesGraph, { Groupings, WidgetType } from 'App/components/ClientSales/SalesGraph/SalesGraph';
import {
	fetcherOrders,
	fetcherAgreements,
	getSummedOrders,
	getSummedAgreements,
	fetcherARRChanges,
	getSummedChanges,
	fetcherClients
} from './helpers';
import { useCompanyGroupSelector } from '../Context/CompanyGroupContext';
import { useFeatureAvailable, useGetData } from 'App/components/hooks';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import DefaultNoData from 'App/components/ListView/DefaultNoData';
import SubscriptionCards from 'App/components/SubscriptionCards';
import { currencyFormat } from 'Components/Filters/Currencies';
import { useTranslation } from 'Components/Helpers/translate';
import { useMetadata } from 'App/components/hooks/appHooks';
import { Feature } from 'App/components/hooks/featureHelper';
import { NumberFormat } from 'App/babel/utils/numberFormat';
import GroupSubscriptionList from './GroupSubscriptionList';
import BemClass from '@upsales/components/Utils/bemClass';
import { Colors } from '@upsales/components/Utils/colors';
import { Toggle, Flex, Text } from '@upsales/components';
import GroupCompanyToggle from './GroupCompanyToggle';
import Agreement from 'App/resources/Model/Agreement';
import { ARRChange } from 'App/resources/Model/ARRChange';
import SalesTarget from './SalesTarget/SalesTarget';
import ButtonSelectGroup from './ButtonSelectGroup';
import { Quota } from 'App/resources/Model/Client';
import Client from 'App/resources/Model/Client';
import Order from 'App/resources/Model/Order';
import GrowthGrid from './GrowthGrid';
import SalesList from './SalesList';
import BigNumber from './BigNumber';

import './SalesTabGroup.scss';
import { getClientQuotaType } from '../../../components/ClientSales/SalesGraph';

type Props = {
	clientIds: number[];
	isGroupBySubAccount?: boolean;
	client?: Client;
};

export type BigNumberProps = {
	value: string;
	label: string;
	loading?: boolean;
	color?: Colors;
};

export type FetcherProps = {
	clientIds: number[];
	isGroupBySubAccount: boolean;
	hasRecurring?: boolean;
};

const LIMIT = 1000;

export const Detached = ({ clientIds: initialClientIds, client, isGroupBySubAccount = false }: Props) => {
	const metadata = useMetadata();
	const { accountSelf } = useSelector(({ App }: RootState) => App);
	const currency = metadata?.defaultCurrency || { iso: 'SEK', rate: 1 };
	const { SalesModelOption, SalesModel: salesModel, brokenFiscalYearEnabled } = metadata?.params || {};
	const hasFiscalYear = brokenFiscalYearEnabled && accountSelf?.features.brokenFiscalYear;
	const { t } = useTranslation();
	const hasRecurring = salesModel === 'rr';
	const hasMRR = hasRecurring && SalesModelOption === 'mrr';
	const hasCM = salesModel === 'cm';
	const hasSales = salesModel === 'sales';
	const numberFormat = new NumberFormat();
	const [grouping, setGrouping] = useState<Groupings>(hasFiscalYear ? Groupings.FISCAL_YEAR : Groupings.YEAR);
	const [groupByCompany, setGroupByCompany] = useState(false);
	const [clientIds, setClientIds] = useState<number[]>([]);
	const [clientQuotasIncludingSubaccounts, setClientQuotasIncludingSubaccounts] = useState<Quota[]>([]);

	const groupingLabel = isGroupBySubAccount ? t('account.subaccount') : t('company');
	const hasClientTarget = useFeatureAvailable(Feature.CLIENT_TARGET);
	const hasRecurringOrder = useFeatureAvailable(Feature.RECURRING_ORDER);
	const hasClientRecurringOrdersFeature = useFeatureAvailable(Feature.CLIENT_RECURRING_ORDERS);
	const operationalAccountId = client?.operationalAccount?.id;
	const numberOfSubaccounts = client?.numberOfSubaccounts;

	let initialWidgetType: WidgetType;
	if (salesModel === 'cm') {
		initialWidgetType = WidgetType.CONTRIBUTION_MARGIN;
	} else if (hasRecurring) {
		initialWidgetType = WidgetType.TOTAL_RECURRING;
	} else {
		initialWidgetType = WidgetType.CASH_FLOW;
	}

	const [widgetType, setWidgetType] = useState<WidgetType>(initialWidgetType);

	useGetData<FetcherProps, Client>({
		fetcher: fetcherClients,
		fetcherProps: {
			clientIds: initialClientIds,
			isGroupBySubAccount
		},
		onFetched: ({ data: clients }) => {
			setClientQuotasIncludingSubaccounts(clients.flatMap(client => client.clientQuotas));
			const ids: number[] = [];
			clients.forEach(client => {
				ids.push(client.id);
			});
			setClientIds(ids);
		},
		limit: LIMIT,
		broadcastTypes: ['account.updated']
	});

	const { data: orders, loading: loadingOrders } = useGetData<FetcherProps, Order>({
		fetcher: fetcherOrders,
		fetcherProps: {
			clientIds: initialClientIds,
			isGroupBySubAccount
		},
		limit: LIMIT,
		broadcastTypes: ['order.added', 'order.updated', 'order.deleted', 'opportunity.deleted']
	});

	const { data: agreements, loading: loadingAgreements } = useGetData<FetcherProps, Agreement>({
		fetcher: fetcherAgreements,
		fetcherProps: {
			clientIds: initialClientIds,
			isGroupBySubAccount,
			hasRecurring
		},
		limit: LIMIT,
		broadcastTypes: [
			'agreementGroup.added',
			'agreementGroup.updated',
			'agreementGroup.deleted',
			'agreement.deleted',
			'agreement.added',
			'agreement.updated'
		]
	});

	const { data: agreementChanges, loading: loadingChanges } = useGetData<FetcherProps, ARRChange>({
		fetcher: fetcherARRChanges,
		fetcherProps: {
			clientIds: initialClientIds,
			isGroupBySubAccount,
			hasRecurring
		},
		limit: LIMIT,
		broadcastTypes: [
			'agreementGroup.added',
			'agreementGroup.updated',
			'agreementGroup.deleted',
			'agreement.deleted',
			'agreement.added',
			'agreement.updated'
		]
	});

	const clientIdsInAgreement = agreements.map(agreement => agreement.client.id);
	const relatedClientIds = clientIdsInAgreement.filter(id => !clientIds.includes(id));

	const { sumOrder, sumCM, sumOrderThisYear, sumCMThisYear } = orders.length
		? getSummedOrders(orders, currency)
		: { sumOrder: 0, sumCM: 0, sumOrderThisYear: 0, sumCMThisYear: 0 };
	const sumARR = agreements.length ? getSummedAgreements(agreements, currency) : 0;
	const { sumChangesARR, sumChangesMRR, sumChangesARRThisYear, sumChangesMRRThisYear } = agreementChanges.length
		? getSummedChanges(agreementChanges, currency)
		: { sumChangesARR: 0, sumChangesMRR: 0, sumChangesARRThisYear: 0, sumChangesMRRThisYear: 0 };

	const salesValues = {
		arr: sumARR,
		sales: sumOrder,
		salesThisYear: sumOrderThisYear,
		cm: sumCM,
		cmThisYear: sumCMThisYear,
		mrr: sumARR / 12,
		arrChange: sumChangesARR,
		mrrChange: sumChangesMRR,
		arrChangeThisYear: sumChangesARRThisYear,
		mrrChangeThisYear: sumChangesMRRThisYear
	};

	let realGrouping = grouping;
	if (widgetType === WidgetType.PRODUCT) {
		realGrouping = Groupings.PRODUCT;
	}

	let realWidgetType = widgetType;
	if (widgetType === WidgetType.PRODUCT && salesModel !== 'rr') {
		realWidgetType = WidgetType.CASH_FLOW;
	}

	const showClientName = !operationalAccountId && numberOfSubaccounts !== 0;

	const recurringWidgets = [WidgetType.RECURRING, WidgetType.TOTAL_RECURRING];
	let content: React.ReactNode = null;
	if (widgetType === WidgetType.PRODUCT) {
		content = (
			<GrowthGrid
				clientIds={clientIds}
				setGrouping={setGrouping}
				grouping={grouping}
				isGroupBySubAccount={isGroupBySubAccount}
			/>
		);
	} else if (
		clientIds.length &&
		(hasRecurringOrder || hasClientRecurringOrdersFeature) &&
		(recurringWidgets.includes(widgetType) || !hasRecurring)
	) {
		if (groupByCompany) {
			content = (
				<GroupSubscriptionList
					clientIds={clientIds}
					isGroupBySubAccount={isGroupBySubAccount}
					groupCompanyToggle={
						<Flex space="pll prl">
							<Toggle
								checked={groupByCompany}
								onChange={() => setGroupByCompany(!groupByCompany)}
								space="mrm"
							/>
							<Text>{`${t('default.groupBy')} ${groupingLabel.toLowerCase()}`}</Text>
						</Flex>
					}
					client={client}
					totalArr={sumARR}
					relatedClientIds={relatedClientIds}
				/>
			);
		} else {
			content = (
				<SubscriptionCards
					showClientName={showClientName}
					hasMRR={hasMRR}
					clientId={client?.id ?? clientIds[0]}
					client={client}
					subAccountIds={client ? clientIds.filter(id => id !== client.id) : [...clientIds].splice(1)}
					isGroupBySubAccount={isGroupBySubAccount}
					groupCompanyToggle={
						showClientName ? (
							<GroupCompanyToggle
								groupByCompany={groupByCompany}
								onChange={() => setGroupByCompany(!groupByCompany)}
								groupingLabel={groupingLabel}
							/>
						) : null
					}
				/>
			);
		}
	}

	const listContent = useMemo(() => {
		switch (widgetType) {
			case WidgetType.PRODUCT:
				return null;
			default:
				return clientIds.length ? (
					<SalesList
						clientIds={clientIds}
						widgetType={realWidgetType}
						grouping={realGrouping}
						isGroupBySubAccount={isGroupBySubAccount}
						isSubaccount={!!operationalAccountId}
					/>
				) : null;
		}
	}, [widgetType, realGrouping, clientIds]);

	const classes = new BemClass('SalesTabGroup');

	const quotaType = getClientQuotaType(widgetType, hasCM, hasRecurring);

	let bigNumbers: BigNumberProps[] = [
		{
			loading: loadingAgreements,
			value: currencyFormat(hasMRR ? salesValues.mrr : salesValues.arr ?? 0, currency.iso),
			label: hasMRR ? t('default.currentMRR') : t('default.currentARR')
		},
		{
			loading: loadingChanges,
			value: `${(hasMRR ? salesValues.mrrChange : salesValues.arrChange ?? 0) >= 0 ? '+' : ''}${currencyFormat(
				hasMRR ? salesValues.mrrChange : salesValues.arrChange ?? 0,
				currency.iso
			)}`,
			label: hasMRR ? t('account.recurring.mrr12months') : t('account.recurring.arr12months'),
			color: (hasMRR ? salesValues.mrrChange : salesValues.arrChange ?? 0) >= 0 ? 'success-5' : 'red'
		}
	];

	if (quotaType === 'sales') {
		bigNumbers = [
			{
				loading: loadingOrders,
				value: currencyFormat(salesValues.sales ?? 0, currency.iso),
				label: t('default.sales12Months')
			}
		];
	} else if (quotaType === 'contributionMargin') {
		bigNumbers = [
			{
				loading: loadingOrders,
				value: currencyFormat(salesValues.sales ?? 0, currency.iso),
				label: t('default.sales12Months')
			},
			{
				loading: loadingOrders,
				value: currencyFormat(salesValues.cm ?? 0, currency.iso),
				label: t('default.contributionMargin12Months')
			},
			{
				loading: loadingOrders,
				value: numberFormat.percent((salesValues.cm ?? 0) / (salesValues.sales || 1)),
				label: t('default.contributionMarginRatio12Months')
			}
		];
	}

	return (
		<Flex direction="column" className={classes.b()}>
			<Flex direction="column" className={classes.elem('topSection').b()} space="ptl pbxl">
				<Flex>
					<Flex
						direction="column"
						gap="u8"
						space="plxl prxl"
						className={classes.elem('topSection').elem('bigNumbers').b()}
					>
						{bigNumbers.map(({ value, label, loading, color }, index) => (
							<BigNumber key={index} value={value} label={label} loading={loading} color={color} />
						))}
						{isGroupBySubAccount && client && hasClientTarget ? (
							<SalesTarget
								quotas={client.clientQuotas}
								quotasIncludingSubaccounts={clientQuotasIncludingSubaccounts}
								clientId={client.id}
								currentQuotaType={quotaType}
								salesThisYear={salesValues.salesThisYear}
								cmThisYear={salesValues.cmThisYear}
								arrThisYear={salesValues.arrChangeThisYear}
								mrrThisYear={salesValues.mrrChangeThisYear}
							/>
						) : null}
					</Flex>
					{initialClientIds.length ? (
						<Flex
							direction="column"
							className={classes.elem('topSection').elem('widget').b()}
							space="ptl mrxl"
						>
							<ButtonSelectGroup
								grouping={grouping}
								widgetType={widgetType}
								onGroupingChange={v => {
									if (
										hasSales &&
										(v === Groupings.PRODUCT ||
											v === Groupings.PRODUCT_CATEGORY ||
											v === Groupings.ROOT_PRODUCT_CATEGORY)
									) {
										setWidgetType(WidgetType.PRODUCT);
									} else if (hasSales) {
										setWidgetType(WidgetType.CASH_FLOW);
									}
									setGrouping(v);
								}}
								onWidgetChange={v => {
									setWidgetType(v);
									if (v === WidgetType.PRODUCT) {
										setGrouping(Groupings.PRODUCT);
									} else {
										setGrouping(hasFiscalYear ? Groupings.FISCAL_YEAR : Groupings.YEAR);
									}
								}}
							/>
							<SalesGraph
								height={2}
								hideGoal={!client}
								grouping={grouping}
								widgetType={realWidgetType}
								clientId={initialClientIds[0]}
								subAccountIds={[...initialClientIds].splice(1)}
								isGroupBySubAccount={isGroupBySubAccount}
							/>
						</Flex>
					) : null}
				</Flex>
			</Flex>
			{initialClientIds.length ? (
				<Flex direction="column">
					{content}
					{listContent}
				</Flex>
			) : (
				<Flex justifyContent="center">
					<DefaultNoData subtitle="" formatNoData={() => t('default.noResults')} />
				</Flex>
			)}
		</Flex>
	);
};

const SalesTab = ({ ...props }: Omit<Props, 'clientIds'>) => {
	const { clientIds } = useCompanyGroupSelector(({ clientIds }) => ({
		clientIds
	}));

	return <Detached {...props} clientIds={clientIds} />;
};

export default SalesTab;
